import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { journals } from "../lib/api";
import useSubscriptionStatus from "../lib/useSubscriptionStatus";
import { toast } from "sonner";
import PaywallModal from "../components/PaywallModal";
import {
  BookHeart,
  Plus,
  Pencil,
  Trash2,
  Lock,
  Check,
  CalendarDays,
  ArrowRight,
  X,
} from "lucide-react";

const MAX_CHARS = 10000;
const READ_ONLY_REASON =
  "Your LifeGuide journey is saved. Upgrade to LifeGuide Pro to keep writing in your Journal.";

function useHasWriteAccess() {
  return useSubscriptionStatus().isPaid;
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) + " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function formatWeekRange(startISO, endISO) {
  try {
    const s = new Date(startISO + "T00:00:00");
    const e = new Date(endISO + "T00:00:00");
    const sMonth = s.toLocaleDateString(undefined, { month: "long" });
    const eMonth = e.toLocaleDateString(undefined, { month: "long" });
    if (sMonth === eMonth) {
      return `${sMonth} ${s.getDate()}–${e.getDate()}`;
    }
    return `${sMonth} ${s.getDate()}–${eMonth} ${e.getDate()}`;
  } catch {
    return `${startISO} – ${endISO}`;
  }
}

function isCurrentWeek(startISO) {
  if (!startISO) return false;
  try {
    const d = new Date();
    const day = d.getDay();
    const offset = day === 0 ? 6 : day - 1;
    d.setUTCDate(d.getUTCDate() - offset);
    return d.toISOString().slice(0, 10) === startISO;
  } catch {
    return false;
  }
}

const SOURCE_LABELS = {
  manual: null,
  coach: "From Coach",
  guidance: "From Guidance",
  weekly_reflection: "Weekly Reflection",
};

function Editor({ initial, onCancel, onSubmit, busy }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [content, setContent] = useState(initial?.content || "");
  const areaRef = useRef(null);

  useEffect(() => {
    areaRef.current?.focus();
  }, []);

  const remaining = MAX_CHARS - content.length;
  const overflow = remaining < 0;

  const submit = (e) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error("Write something before saving.");
      return;
    }
    if (overflow) {
      toast.error(`Please shorten to ${MAX_CHARS.toLocaleString()} characters.`);
      return;
    }
    onSubmit({ title: title.trim() || null, content: trimmed });
  };

  return (
    <form
      onSubmit={submit}
      data-testid="journal-editor"
      className="rounded-3xl border border-[#DCE5DB] bg-white p-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up"
    >
      <input
        data-testid="journal-title-input"
        value={title}
        onChange={(e) => setTitle(e.target.value.slice(0, 120))}
        placeholder="Title (optional)"
        className="w-full bg-transparent border-0 focus:outline-none focus:ring-0 font-display text-xl text-[#2E2E2E] placeholder:text-[#a8a8a8] mb-2"
      />
      <textarea
        ref={areaRef}
        data-testid="journal-content-input"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="What's on your heart today?"
        rows={8}
        className="w-full resize-none bg-transparent border-0 focus:outline-none focus:ring-0 text-[15.5px] leading-[1.75] text-[#2E2E2E] placeholder:text-[#a8a8a8]"
      />
      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-[#DCE5DB]">
        <span
          data-testid="journal-char-count"
          className={`text-xs ${overflow ? "text-[#A86B4A] font-medium" : "text-[#6B6B6B]"}`}
        >
          {content.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="journal-cancel-btn"
            className="text-sm text-[#6B6B6B] hover:text-[#2E2E2E] rounded-full px-4 py-2 hover:bg-[#EEF3EC] transition inline-flex items-center gap-1.5"
          >
            <X size={14} /> Cancel
          </button>
          <button
            type="submit"
            disabled={busy || overflow || !content.trim()}
            data-testid="journal-save-btn"
            className="bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-50 text-white rounded-full px-5 py-2 text-sm font-medium transition inline-flex items-center gap-1.5 shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
          >
            <Check size={14} /> {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </form>
  );
}

function ConfirmDeleteDialog({ open, onCancel, onConfirm, busy }) {
  if (!open) return null;
  return (
    <div
      data-testid="journal-confirm-delete"
      className="fixed inset-0 z-[100] bg-[#2E2E2E]/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-[0_-8px_40px_rgba(0,0,0,0.12)]"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-display text-xl text-[#2E2E2E] mb-2">Delete this entry?</h3>
        <p className="text-sm text-[#6B6B6B] leading-relaxed mb-5">
          This will permanently remove this journal entry. This can't be undone.
        </p>
        <div className="flex gap-2 justify-end">
          <button
            data-testid="journal-delete-cancel"
            onClick={onCancel}
            className="text-sm text-[#6B6B6B] hover:text-[#2E2E2E] rounded-full px-4 py-2 hover:bg-[#EEF3EC] transition"
          >
            Keep it
          </button>
          <button
            data-testid="journal-delete-confirm"
            onClick={onConfirm}
            disabled={busy}
            className="bg-[#A86B4A] hover:bg-[#8f5a3d] disabled:opacity-60 text-white rounded-full px-5 py-2 text-sm font-medium transition inline-flex items-center gap-1.5"
          >
            <Trash2 size={13} /> {busy ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EntryCard({ entry, canWrite, onEdit, onDelete, onLockedWrite }) {
  const sourceLabel = SOURCE_LABELS[entry.source];
  const isWeekly = entry.source === "weekly_reflection";
  // For weekly-reflection entries, display a human title + friendly week
  // range instead of the stored "Week of YYYY-MM-DD" tech title. The
  // underlying stored record is unchanged.
  const displayTitle = isWeekly
    ? isCurrentWeek(entry.week_start)
      ? "This Week's Reflection"
      : "Weekly Reflection"
    : entry.title;
  const weekRangeLabel =
    isWeekly && entry.week_start && entry.week_end
      ? formatWeekRange(entry.week_start, entry.week_end)
      : null;
  return (
    <article
      data-testid={`journal-entry-${entry.id}`}
      className={`rounded-3xl border p-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)] fade-in-up ${
        isWeekly
          ? "border-[#B7CCB1] bg-[#EEF3EC]"
          : "border-[#DCE5DB] bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          {displayTitle ? (
            <h3
              data-testid={isWeekly ? "journal-weekly-entry-title" : undefined}
              className="font-display text-lg text-[#2E2E2E] leading-snug truncate"
            >
              {displayTitle}
            </h3>
          ) : null}
          {weekRangeLabel && (
            <p
              data-testid="journal-weekly-entry-range"
              className="text-[13px] font-medium text-[#3F5A42] mt-0.5"
            >
              {weekRangeLabel}
            </p>
          )}
          <div className="text-xs text-[#6B6B6B] mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{formatWhen(entry.created_at)}</span>
            {sourceLabel && (
              <span
                data-testid={`journal-source-badge-${entry.source}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  isWeekly
                    ? "bg-[#4F6F52] text-white"
                    : "bg-[#EEF3EC] text-[#4F6F52]"
                }`}
              >
                {isWeekly && <CalendarDays size={10} strokeWidth={2.4} />}
                {sourceLabel}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            data-testid={`journal-edit-${entry.id}`}
            onClick={() => (canWrite ? onEdit(entry) : onLockedWrite())}
            className="p-2 rounded-full text-[#6B6B6B] hover:text-[#4F6F52] hover:bg-[#EEF3EC] transition"
            aria-label="Edit entry"
            title={canWrite ? "Edit" : "Upgrade to edit"}
          >
            <Pencil size={15} />
          </button>
          <button
            data-testid={`journal-delete-${entry.id}`}
            onClick={() => (canWrite ? onDelete(entry) : onLockedWrite())}
            className="p-2 rounded-full text-[#6B6B6B] hover:text-[#A86B4A] hover:bg-[#F5E6D3]/60 transition"
            aria-label="Delete entry"
            title={canWrite ? "Delete" : "Upgrade to delete"}
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      <p className="text-[15px] leading-[1.75] text-[#2E2E2E] whitespace-pre-wrap">
        {entry.content}
      </p>
    </article>
  );
}

export default function JournalPage() {
  const canWrite = useHasWriteAccess();
  const [entries, setEntries] = useState(null);
  const [mode, setMode] = useState("list"); // "list" | "create" | "edit"
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [paywall, setPaywall] = useState({ open: false, reason: "" });
  const navigate = useNavigate();

  const load = async () => {
    try {
      const rows = await journals.list();
      setEntries(rows);
    } catch {
      setEntries([]);
      toast.error("Couldn't load your journal");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openPaywall = () =>
    setPaywall({ open: true, reason: READ_ONLY_REASON });

  // Detect whether the user has already saved this week's Weekly Reflection so
  // the promo card can flip to "View This Week's Reflection ✓" and route to the
  // existing entry (dedupe-safe — never creates a second one).
  const thisWeekMonday = useMemo(() => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun..6=Sat
    const offset = day === 0 ? 6 : day - 1; // days since Monday
    d.setUTCDate(d.getUTCDate() - offset);
    return d.toISOString().slice(0, 10);
  }, []);
  const currentWeekReflection = useMemo(() => {
    if (!entries) return null;
    return (
      entries.find(
        (e) => e.source === "weekly_reflection" && e.week_start === thisWeekMonday,
      ) || null
    );
  }, [entries, thisWeekMonday]);

  const startCreate = () => {
    if (!canWrite) return openPaywall();
    setEditing(null);
    setMode("create");
  };

  const startEdit = (entry) => {
    // Weekly Reflection entries are edited on their dedicated screen so all
    // five prompts and the LifeGuide Reflection remain structured.
    if (entry.source === "weekly_reflection") {
      navigate("/journal/weekly-reflection");
      return;
    }
    setEditing(entry);
    setMode("edit");
  };

  const cancelEditor = () => {
    setEditing(null);
    setMode("list");
  };

  const handleCreate = async ({ title, content }) => {
    setBusy(true);
    try {
      const created = await journals.create({ title, content, source: "manual" });
      setEntries((rows) => [created, ...(rows || [])]);
      setMode("list");
      setEditing(null);
      toast.success("Saved to your journal");
    } catch (err) {
      if (err?.response?.status === 402) {
        openPaywall();
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't save entry");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleUpdate = async ({ title, content }) => {
    if (!editing) return;
    setBusy(true);
    try {
      const updated = await journals.update(editing.id, { title, content });
      setEntries((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
      setMode("list");
      setEditing(null);
      toast.success("Entry updated");
    } catch (err) {
      if (err?.response?.status === 402) {
        openPaywall();
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't update entry");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await journals.remove(confirmDel.id);
      setEntries((rows) => rows.filter((r) => r.id !== confirmDel.id));
      setConfirmDel(null);
      toast.success("Entry deleted");
    } catch (err) {
      if (err?.response?.status === 402) {
        setConfirmDel(null);
        openPaywall();
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't delete entry");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="fade-in-up">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-2xl bg-[#E1ECDD] text-[#4F6F52] flex items-center justify-center">
            <BookHeart size={17} strokeWidth={1.7} />
          </span>
          <h1 className="font-display text-3xl text-[#2E2E2E]">Journal</h1>
        </div>
        <p className="text-sm text-[#6B6B6B] leading-relaxed mt-2">
          A private space for your thoughts, reflections, and prayers. Only you can see this.
        </p>
      </header>

      {!canWrite && (
        <div
          data-testid="journal-readonly-banner"
          className="rounded-3xl border border-[#D6C7A1]/60 bg-[#F5EBD9]/50 p-5 flex items-start gap-3 fade-in-up"
        >
          <span className="w-9 h-9 shrink-0 rounded-2xl bg-white/70 text-[#8C7A4F] flex items-center justify-center">
            <Lock size={15} />
          </span>
          <div className="flex-1">
            <p className="font-medium text-[#2E2E2E] text-[15px]">
              Your journal is saved and read-only
            </p>
            <p className="text-sm text-[#6B6B6B] leading-relaxed mt-1">
              Upgrade to LifeGuide Pro to keep writing new entries. Everything you wrote is preserved.
            </p>
            <button
              data-testid="journal-upgrade-btn"
              onClick={openPaywall}
              className="mt-3 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-2 text-sm font-medium transition shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
            >
              Continue with LifeGuide Pro
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => (canWrite ? navigate("/journal/weekly-reflection") : openPaywall())}
        data-testid="journal-weekly-reflection-card"
        aria-label={
          currentWeekReflection
            ? "View this week's reflection"
            : "Start this week's reflection"
        }
        className={`group w-full text-left rounded-3xl border p-5 shadow-[0_4px_14px_rgba(79,111,82,0.12)] hover:shadow-[0_6px_18px_rgba(79,111,82,0.18)] transition fade-in-up ${
          currentWeekReflection
            ? "border-[#4F6F52] bg-gradient-to-br from-[#D8E7D3] to-[#C4DABB]"
            : "border-[#B7CCB1] bg-gradient-to-br from-[#EEF3EC] to-[#E1ECDD]"
        }`}
      >
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 shrink-0 rounded-2xl bg-[#4F6F52] text-white flex items-center justify-center shadow-[0_4px_10px_rgba(79,111,82,0.25)]">
            {currentWeekReflection ? (
              <Check size={18} strokeWidth={2.6} />
            ) : (
              <CalendarDays size={17} strokeWidth={2.2} />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h2
                data-testid="journal-weekly-reflection-title"
                className="font-display text-lg text-[#2E2E2E]"
              >
                {currentWeekReflection
                  ? "✓ View This Week's Reflection"
                  : "Weekly Reflection"}
              </h2>
              <ArrowRight
                size={16}
                className="text-[#4F6F52] shrink-0 group-hover:translate-x-0.5 transition"
              />
            </div>
            <p className="text-sm text-[#4E5A4C] mt-1 leading-relaxed">
              {currentWeekReflection
                ? "You've already reflected this week. Tap to review or edit what you wrote."
                : "Take a few minutes to look back, notice what stood out, and carry something meaningful into the week ahead."}
            </p>
          </div>
        </div>
      </button>

      {mode === "list" && (
        <div className="flex justify-end fade-in-up">
          <button
            data-testid="journal-new-btn"
            onClick={startCreate}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition shadow-[0_4px_14px_rgba(79,111,82,0.18)] ${
              canWrite
                ? "bg-[#4F6F52] hover:bg-[#3F5A42] text-white"
                : "bg-white border border-[#DCE5DB] text-[#6B6B6B] hover:border-[#4F6F52]/40"
            }`}
          >
            <Plus size={15} /> New entry
          </button>
        </div>
      )}

      {mode === "create" && (
        <Editor
          initial={null}
          onCancel={cancelEditor}
          onSubmit={handleCreate}
          busy={busy}
        />
      )}
      {mode === "edit" && editing && (
        <Editor
          initial={editing}
          onCancel={cancelEditor}
          onSubmit={handleUpdate}
          busy={busy}
        />
      )}

      {entries === null ? (
        <div className="space-y-3">
          <div className="h-28 rounded-3xl shimmer" />
          <div className="h-28 rounded-3xl shimmer" />
        </div>
      ) : entries.length === 0 ? (
        <div
          data-testid="journal-empty-state"
          className="rounded-3xl border border-dashed border-[#DCE5DB] bg-white p-8 text-center fade-in-up"
        >
          <div className="mx-auto w-12 h-12 rounded-2xl bg-[#EEF3EC] text-[#4F6F52] flex items-center justify-center mb-3">
            <BookHeart size={20} strokeWidth={1.6} />
          </div>
          <h3 className="font-display text-xl text-[#2E2E2E] mb-1.5">
            Your journal is quiet
          </h3>
          <p className="text-sm text-[#6B6B6B] max-w-xs mx-auto leading-relaxed">
            {canWrite
              ? "Capture a thought, a prayer, or something you don't want to forget."
              : "Upgrade to LifeGuide Pro to start writing entries only you can see."}
          </p>
          {canWrite && mode === "list" && (
            <button
              data-testid="journal-empty-cta"
              onClick={startCreate}
              className="mt-5 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 transition shadow-[0_4px_14px_rgba(79,111,82,0.18)]"
            >
              <Plus size={14} /> Write your first entry
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="journal-list">
          {entries.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              canWrite={canWrite}
              onEdit={startEdit}
              onDelete={(e) => setConfirmDel(e)}
              onLockedWrite={openPaywall}
            />
          ))}
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!confirmDel}
        onCancel={() => setConfirmDel(null)}
        onConfirm={handleDelete}
        busy={busy}
      />

      <PaywallModal
        open={paywall.open}
        reason={paywall.reason}
        onClose={() => setPaywall({ open: false, reason: "" })}
      />
    </div>
  );
}
